/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Button,
  DETAIL_ACTION_GHOST,
  DETAIL_ACTION_PRIMARY,
  DETAIL_ACTION_SECONDARY,
  Text, Textarea
} from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

import { ConfettiBurst } from './ConfettiBurst';
import { useSaleActionLabel } from '../constants/sale-states';
import type { SaleState } from '../constants/sale-states';

export interface SaleActionRowProps {
  /** Applies a transition. Undoing carries the stated reason. */
  onApply: (target: SaleState, reason?: string) => Promise<void>;
  /** Sends the reader to the tab that owns a form. */
  onGoToTab: (tab: 'commission' | 'paperwork') => void;
}

/**
 * Reads the refusal the backend actually sent.
 *
 * The service deliberately declares no fixed 422 message, so the response
 * body carries something the broker can act on. Discarding it in favour of a
 * generic string is the bug this exists to avoid — the same one that had to
 * be fixed in the lease actions.
 *
 * @param error - The thrown request error.
 * @returns The backend's message, when it sent one.
 */
function refusalReason(error: unknown): string | undefined {
  const body = (error as { body?: { error?: string } } | undefined)?.body;
  return body?.error;
}

/**
 * What a broker can do to a sale that is still in paperwork.
 *
 * Rendered only for `in_paperwork` — both other states are terminal, and the
 * page replaces this row with the outcome card once one is reached.
 *
 * The two recording actions move to the tab that already owns their form
 * rather than opening a second copy of it here. Undoing gets an inline panel
 * instead, because it needs a reason before it can be sent and it cannot be
 * taken back once it is.
 *
 * @param props.onApply - Applies the transition.
 * @param props.onGoToTab - Switches the active tab.
 * @returns The rendered action row.
 */
export function SaleActionRow({ onApply, onGoToTab }: SaleActionRowProps) {
  const { t } = useTranslation('features/sales');
  const actionLabel = useSaleActionLabel();
  const [concluding, setConcluding] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'conclude' | 'void' | null>(null);
  const [celebrating, setCelebrating] = useState(false);

  async function run(target: SaleState, why?: string) {
    setBusy(target === 'voided' ? 'void' : 'conclude');
    try {
      await onApply(target, why);
      toast.success(t('actions.applied'));
      setVoiding(false);
      setConcluding(false);
      setReason('');
      if (target === 'completed') setCelebrating(true);
    } catch (e) {
      toast.error(refusalReason(e) ?? t('actions.failed'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      {/* Recording the paperwork is what actually advances a sale, so it
        * leads and it is the only solid control here. Concluding is a
        * consequence of the milestones being complete, not a shortcut past
        * them, which is why it sits after the rule with the cancel. */}
      <div data-testid="sale-action-row" className="flex flex-wrap items-center gap-(--space-2)">
        <Button
          size="custom"
          variant="none"
          className={DETAIL_ACTION_PRIMARY}
          data-testid="sale-action-set-dates"
          onClick={() => onGoToTab('paperwork')}
        >
          {t('actions.setDates')}
        </Button>

        <Button
          size="custom"
          variant="none"
          className={DETAIL_ACTION_SECONDARY}
          data-testid="sale-action-record-receipt"
          onClick={() => onGoToTab('commission')}
        >
          {t('actions.recordReceipt')}
        </Button>

        <div
          aria-hidden="true"
          className="mx-(--space-1) hidden h-6 w-px self-center bg-(--border-subtle) sm:block"
        />

        <Button
          size="custom"
          variant="none"
          className={DETAIL_ACTION_SECONDARY}
          disabled={busy !== null}
          loading={busy === 'conclude'}
          data-testid="sale-action-conclude"
          onClick={() => setConcluding((open) => !open)}
        >
          {actionLabel('conclude')}
        </Button>

        <Button
          size="custom"
          variant="none"
          disabled={busy !== null}
          data-testid="sale-action-cancel"
          onClick={() => setVoiding((open) => !open)}
          className={cn(DETAIL_ACTION_GHOST, 'text-(--accent-cobre) hover:bg-(--accent-cobre)/10')}
        >
          {actionLabel('cancel')}
        </Button>
      </div>

      {concluding && (
        <div
          className="basis-full flex flex-col gap-(--space-2) border-t border-(--border-subtle) pt-(--space-3)"
          data-testid="sale-conclude-panel"
        >
          <Text component="span" variant="small" fw={600} tone="primary">
            {t('detail.conclude.title')}
          </Text>
          <Text component="span" variant="meta" tone="tertiary">
            {t('detail.conclude.note')}
          </Text>
          <div className="flex flex-wrap items-center gap-(--space-2)">
            <Button
              size="sm"
              variant="solid"
              disabled={busy !== null}
              loading={busy === 'conclude'}
              data-testid="sale-conclude-confirm"
              onClick={() => void run('completed')}
            >
              {t('detail.conclude.confirm')}
            </Button>
            <Button
              size="sm"
              variant="ghost-text"
              data-testid="sale-conclude-dismiss"
              onClick={() => setConcluding(false)}
            >
              {t('detail.conclude.cancel')}
            </Button>
          </div>
        </div>
      )}

      {voiding && (
        <div
          className="basis-full flex flex-col gap-(--space-2) border-t border-(--border-subtle) pt-(--space-3)"
          data-testid="sale-void-panel"
        >
          <Text component="span" variant="small" fw={600} tone="primary">
            {t('detail.void.title')}
          </Text>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('detail.void.reasonPlaceholder')}
            aria-label={t('detail.void.reasonLabel')}
            data-testid="sale-void-reason-input"
            className="min-h-16"
          />
          <Text component="span" variant="meta" tone="tertiary">
            {t('detail.void.note')}
          </Text>
          <div className="flex flex-wrap items-center gap-(--space-2)">
            <Button
              size="sm"
              variant="none"
              disabled={!reason.trim() || busy !== null}
              loading={busy === 'void'}
              data-testid="sale-void-confirm"
              onClick={() => void run('voided', reason.trim())}
              className="bg-(--accent-cobre) text-(--text-inverse) hover:opacity-90"
            >
              {t('detail.void.confirm')}
            </Button>
            <Button
              size="sm"
              variant="ghost-text"
              data-testid="sale-void-dismiss"
              onClick={() => setVoiding(false)}
            >
              {t('detail.void.cancel')}
            </Button>
          </div>
        </div>
      )}
      {celebrating && <ConfettiBurst onDone={() => setCelebrating(false)} />}
    </>
  );
}

export default SaleActionRow;
